import { useState } from "react";
import Toggle from "@/components/base/Toggle";
import { useParametres } from "../ParametresProvider";
import type { AgencePreferences } from "../types";

export default function NotificationsSection() {
  const { preferences, updatePreferences } = useParametres();
  const [form, setForm] = useState<AgencePreferences>(preferences);
  const [saved, setSaved] = useState(false);

  const update = <K extends keyof AgencePreferences>(key: K, value: AgencePreferences[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = () => {
    updatePreferences(form);
    setSaved(true);
  };

  const notifications = [
    { key: "notifLoyer" as const, label: "Loyers", description: "Rappels sur les loyers à encaisser." },
    { key: "notifPaiement" as const, label: "Paiements", description: "Confirmation à chaque paiement enregistré." },
    { key: "notifImpaye" as const, label: "Impayés", description: "Alerte lorsqu'une échéance passe en retard." },
    { key: "notifContrat" as const, label: "Contrats", description: "Rappel avant la fin d'un contrat." },
    { key: "notifEmploye" as const, label: "Employés", description: "Activité et modifications des employés de l'agence." },
  ];

  return (
    <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
      <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
          <i className="ri-notification-3-line text-base" aria-hidden="true" />
        </span>
        <div>
          <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Notifications</h3>
          <p className="text-xs text-foreground-500">Choisissez les événements pour lesquels être alerté.</p>
        </div>
      </header>

      <div className="flex flex-col divide-y divide-background-200 p-4 md:p-5">
        {notifications.map((item) => (
          <div key={item.key} className="py-3.5 first:pt-0">
            <Toggle
              checked={form[item.key]}
              onChange={(value) => update(item.key, value)}
              label={item.label}
              description={item.description}
            />
          </div>
        ))}
        <div className="py-3.5">
          <Toggle
            checked={form.notifEmail}
            onChange={(value) => update("notifEmail", value)}
            label="Recevoir aussi par email"
            description="Un récapitulatif est envoyé à l'adresse email de l'agence."
          />
        </div>
      </div>

      <div className="flex flex-col-reverse gap-3 border-t border-background-200 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-5">
        {saved ? (
          <p className="flex items-center gap-1.5 text-xs text-secondary-700">
            <i className="ri-checkbox-circle-line text-sm" aria-hidden="true" />
            Notifications enregistrées.
          </p>
        ) : (
          <span className="text-xs text-foreground-400">Ces réglages s'appliquent à tout l'espace Agence.</span>
        )}
        <button
          type="button"
          onClick={handleSave}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-save-line text-base" aria-hidden="true" />
          Enregistrer
        </button>
      </div>
    </section>
  );
}